import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import { useCallback, useEffect, useState } from "react";
import { businessClientApiService } from "../../../infrastructure/api/company/business-client-api-service";
import { useParams, useNavigate } from "react-router-dom";
import Table from "../../components/UI/Table";
import type {
  ManagedClientDetail as ManagedClientDetailType,
  UpdateManagedClientRequest,
  CompareField,
  ManagedClientImage,
  OrderFile,
} from "../../../domain/types/business-client";
import { ArrowLeft, Link2, Unlink, Upload, Trash2, Search } from "lucide-react";

const FIELD_LABELS: Record<string, string> = {
  businessName: "업체명",
  businessNumber: "사업자번호",
  businessPhone: "기업 연락처",
  businessType: "업태",
  businessCategory: "업종",
  postcode: "우편번호",
  address: "주소",
  addressDetail: "상세주소",
  taxManagerName: "세금계산서 담당자",
  taxManagerPhone: "담당자 연락처",
  taxEmail: "세금계산서 이메일",
};

const IMAGE_TYPE_LABELS: Record<string, string> = {
  BUSINESS_REGISTRATION: "사업자등록증",
  OTHER: "기타",
};

type TabType = "info" | "images" | "compare" | "orders";

const ManagedClientDetail: React.FC = () => {
  const { companyId, clientId } = useParams();
  const navigate = useNavigate();

  const [partner, setPartner] = useState<ManagedClientDetailType | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>("info");
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<UpdateManagedClientRequest>({ businessName: "" });
  const [compareFields, setCompareFields] = useState<CompareField[]>([]);
  const [matchedUsers, setMatchedUsers] = useState<any[]>([]);
  const [isMatching, setIsMatching] = useState(false);
  const [orders, setOrders] = useState<any[]>([]);
  const [orderFiles, setOrderFiles] = useState<OrderFile[]>([]);
  const [orderSubTab, setOrderSubTab] = useState<"orders" | "files">("orders");

  const applyPartnerData = (data: ManagedClientDetailType) => {
    setPartner(data);
    setEditForm({
      businessName: data.businessName,
      businessNumber: data.businessNumber || undefined,
      businessPhone: data.businessPhone || undefined,
      businessType: data.businessType || undefined,
      businessCategory: data.businessCategory || undefined,
      postcode: data.postcode || undefined,
      address: data.address || undefined,
      addressDetail: data.addressDetail || undefined,
      taxManagerName: data.taxManagerName || undefined,
      taxManagerPhone: data.taxManagerPhone || undefined,
      taxEmail: data.taxEmail || undefined,
    });
  };

  const loadPartner = useCallback(async () => {
    if (!companyId || !clientId) return;
    try {
      const response = await businessClientApiService.getManagedClientDetail(Number(companyId), Number(clientId));
      if (response.data) {
        applyPartnerData(response.data);
      }
    } catch (error) {
      console.error("거래처 상세 조회 실패:", error);
    }
  }, [companyId, clientId]);

  useEffect(() => {
    loadPartner();
  }, [loadPartner]);

  const handleUpdate = async () => {
    if (!companyId || !clientId) return;
    try {
      await businessClientApiService.updateManagedClient(Number(companyId), Number(clientId), editForm);
      setIsEditing(false);
      loadPartner();
    } catch (error) {
      console.error("거래처 수정 실패:", error);
      alert("거래처 수정에 실패했습니다.");
    }
  };

  const handleDelete = async () => {
    if (!companyId || !clientId) return;
    if (!window.confirm("거래처를 삭제하시겠습니까?")) return;
    try {
      await businessClientApiService.deleteManagedClient(Number(companyId), Number(clientId));
      navigate(`/company/${companyId}/business-clients`);
    } catch (error) {
      console.error("거래처 삭제 실패:", error);
      alert("거래처 삭제에 실패했습니다.");
    }
  };

  const handleCompare = async () => {
    if (!companyId || !clientId) return;
    setActiveTab("compare");
    try {
      const response = await businessClientApiService.compareManagedClient(Number(companyId), Number(clientId));
      if (response.data) {
        setCompareFields(response.data.fields);
      }
    } catch (error) {
      console.error("비교 조회 실패:", error);
    }
  };

  const handleMatchUser = async () => {
    if (!companyId || !clientId) return;
    setIsMatching(true);
    try {
      const response = await httpGet(`/api/company/${companyId}/managed-clients/${clientId}/match-user`);
      setMatchedUsers(response?.data?.matchedUsers || []);
    } catch (error) {
      console.error("유저 매칭 실패:", error);
    }
    setIsMatching(false);
  };

  const handleLinkUser = async (userId: number) => {
    if (!companyId || !clientId) return;
    try {
      await businessClientApiService.linkUser(Number(companyId), Number(clientId), userId);
      setMatchedUsers([]);
      loadPartner();
    } catch (error) {
      console.error("유저 연결 실패:", error);
      alert("유저 연결에 실패했습니다.");
    }
  };

  const handleUnlinkUser = async () => {
    if (!companyId || !clientId) return;
    if (!window.confirm("회원 연결을 해제하시겠습니까?")) return;
    try {
      await businessClientApiService.unlinkUser(Number(companyId), Number(clientId));
      loadPartner();
    } catch (error) {
      console.error("유저 연결 해제 실패:", error);
      alert("유저 연결 해제에 실패했습니다.");
    }
  };

  const handleImageUpload = async (file: File, imageType: string) => {
    if (!companyId || !clientId) return;
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("imageType", imageType);
      // TODO: API 연동
      alert("이미지 업로드 기능은 백엔드 배포 후 사용 가능합니다.");
    } catch (error) {
      console.error("이미지 업로드 실패:", error);
    }
  };

  const handleImageDelete = async (imageId: number) => {
    if (!window.confirm("이미지를 삭제하시겠습니까?")) return;
    // TODO: API 연동
    alert("이미지 삭제 기능은 백엔드 배포 후 사용 가능합니다.");
  };

  const loadOrders = useCallback(async () => {
    if (!companyId || !clientId) return;
    try {
      const response = await businessClientApiService.getManagedClientOrders(
        Number(companyId),
        Number(clientId),
        { page: 0, size: 50 },
      );
      if (response.data) {
        setOrders(response.data.items || []);
      }
    } catch (error) {
      console.error("거래처 주문내역 조회 실패:", error);
      setOrders([]);
    }
  }, [companyId, clientId]);

  const loadOrderFiles = useCallback(async () => {
    if (!companyId || !clientId) return;
    try {
      const response = await businessClientApiService.getManagedClientOrderFiles(
        Number(companyId),
        Number(clientId),
        0,
        50,
      );
      if (response.data) {
        setOrderFiles(response.data.items || []);
      }
    } catch (error) {
      console.error("거래처 주문 파일 조회 실패:", error);
      setOrderFiles([]);
    }
  }, [companyId, clientId]);

  useEffect(() => {
    if (activeTab === "orders" && partner?.userId) {
      if (orderSubTab === "orders") loadOrders();
      if (orderSubTab === "files") loadOrderFiles();
    }
  }, [activeTab, orderSubTab, partner?.userId, loadOrders, loadOrderFiles]);

  // TODO: API 연동 후 httpClient 사용으로 변경
  const httpGet = async (url: string) => {
    const { httpClient } = await import("../../../infrastructure/api/http-client");
    const response = await httpClient.get(url);
    return response.data;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-";
    return new Date(dateStr)
      .toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" })
      .replace(/-/g, ". ");
  };

  if (!partner) {
    return (
      <div className="relative">
        <CompanyAdminLayout>
          <div className="p-5 text-center text-[#888]">로딩 중...</div>
        </CompanyAdminLayout>
      </div>
    );
  }

  const tabClass = (tab: string) =>
    `px-4 py-2 text-[13px] font-medium cursor-pointer border-b-2 ${
      activeTab === tab
        ? "border-[#4285F4] text-[#4285F4]"
        : "border-transparent text-[#888] hover:text-[#555]"
    }`;

  return (
    <div className="relative">
      <CompanyAdminLayout>
        <div className="p-5">
          {/* 헤더 */}
          <div className="mb-5 flex items-center gap-3">
            <button
              onClick={() => navigate(`/company/${companyId}/business-clients`)}
              className="p-1 hover:bg-[#f5f5f5] rounded"
            >
              <ArrowLeft size={20} />
            </button>
            <h2 className="text-[1.375rem] font-semibold">{partner.businessName}</h2>
            <span
              className={`inline-block px-2 py-0.5 rounded text-[11px] ${
                partner.userId
                  ? "bg-[#E8EFFF] text-[#4285F4]"
                  : "bg-[#f5f5f5] text-[#888]"
              }`}
            >
              {partner.userId ? `회원 연결됨 (ID: ${partner.userId})` : "미연결"}
            </span>
          </div>

          {/* 탭 */}
          <div className="flex gap-0 border-b border-[#eee] mb-5">
            <div className={tabClass("info")} onClick={() => setActiveTab("info")}>기업 정보</div>
            <div className={tabClass("images")} onClick={() => setActiveTab("images")}>첨부 파일</div>
            <div className={tabClass("compare")} onClick={handleCompare}>정보 비교</div>
            <div className={tabClass("orders")} onClick={() => setActiveTab("orders")}>거래 내역</div>
          </div>

          {/* 기업 정보 탭 */}
          {activeTab === "info" && (
            <div className="bg-white border border-[#e0e0e0] rounded-[12px] p-5">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-[15px] font-medium">기업 정보</h3>
                <div className="flex gap-2">
                  {isEditing ? (
                    <>
                      <button onClick={() => setIsEditing(false)} className="px-3 py-1.5 text-[12px] border border-[#222] text-[#222] rounded-[6px] hover:bg-[#f5f5f5]">취소</button>
                      <button onClick={handleUpdate} className="px-3 py-1.5 text-[12px] bg-[#222] text-white rounded-[6px] hover:bg-[#333]">저장</button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => setIsEditing(true)} className="px-3 py-1.5 text-[12px] bg-[#222] text-white rounded-[6px] hover:bg-[#333]">수정</button>
                      <button onClick={handleDelete} className="px-3 py-1.5 text-[12px] text-[#F24A4A] border border-[#F24A4A] rounded-[6px] hover:bg-[#FEF2F2]">삭제</button>
                    </>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-3">
                {isEditing ? (
                  <>
                    <EditField label="업체명" value={editForm.businessName} onChange={(v) => setEditForm({ ...editForm, businessName: v })} />
                    <EditField label="사업자번호" value={editForm.businessNumber || ""} onChange={(v) => setEditForm({ ...editForm, businessNumber: v })} />
                    <EditField label="기업 연락처" value={editForm.businessPhone || ""} onChange={(v) => setEditForm({ ...editForm, businessPhone: v })} />
                    <EditField label="업태" value={editForm.businessType || ""} onChange={(v) => setEditForm({ ...editForm, businessType: v })} />
                    <EditField label="업종" value={editForm.businessCategory || ""} onChange={(v) => setEditForm({ ...editForm, businessCategory: v })} />
                    <EditField label="우편번호" value={editForm.postcode || ""} onChange={(v) => setEditForm({ ...editForm, postcode: v })} />
                    <EditField label="주소" value={editForm.address || ""} onChange={(v) => setEditForm({ ...editForm, address: v })} />
                    <EditField label="상세주소" value={editForm.addressDetail || ""} onChange={(v) => setEditForm({ ...editForm, addressDetail: v })} />
                    <EditField label="세금계산서 담당자" value={editForm.taxManagerName || ""} onChange={(v) => setEditForm({ ...editForm, taxManagerName: v })} />
                    <EditField label="담당자 연락처" value={editForm.taxManagerPhone || ""} onChange={(v) => setEditForm({ ...editForm, taxManagerPhone: v })} />
                    <EditField label="세금계산서 이메일" value={editForm.taxEmail || ""} onChange={(v) => setEditForm({ ...editForm, taxEmail: v })} />
                  </>
                ) : (
                  <>
                    <InfoRow label="업체명" value={partner.businessName} />
                    <InfoRow label="사업자번호" value={partner.businessNumber} />
                    <InfoRow label="기업 연락처" value={partner.businessPhone} />
                    <InfoRow label="업태" value={partner.businessType} />
                    <InfoRow label="업종" value={partner.businessCategory} />
                    <InfoRow label="우편번호" value={partner.postcode} />
                    <InfoRow label="주소" value={partner.address} />
                    <InfoRow label="상세주소" value={partner.addressDetail} />
                    <InfoRow label="세금계산서 담당자" value={partner.taxManagerName} />
                    <InfoRow label="담당자 연락처" value={partner.taxManagerPhone} />
                    <InfoRow label="세금계산서 이메일" value={partner.taxEmail} />
                    <InfoRow label="등록일" value={formatDate(partner.createdAt)} />
                    <InfoRow label="수정일" value={formatDate(partner.updatedAt)} />
                  </>
                )}
              </div>

              {/* 회원 연결 영역 */}
              <div className="mt-6 pt-4 border-t border-[#eee]">
                <h4 className="text-[14px] font-medium mb-3">회원 연결</h4>
                {partner.userId ? (
                  <div className="flex items-center gap-3">
                    <span className="text-[13px] text-[#555]">연결된 회원 ID: {partner.userId}</span>
                    <button
                      onClick={handleUnlinkUser}
                      className="flex items-center gap-1 px-3 py-1.5 text-[12px] text-[#F24A4A] border border-[#F24A4A] rounded-[6px] hover:bg-[#FEF2F2]"
                    >
                      <Unlink size={14} />연결 해제
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={handleMatchUser}
                      disabled={isMatching}
                      className="flex items-center gap-1 px-4 py-2 text-[13px] bg-[#4285F4] text-white rounded-[6px] hover:bg-[#3367D6] disabled:bg-[#ccc] w-fit"
                    >
                      <Search size={14} />
                      {isMatching ? "검색 중..." : "사업자번호로 회원 자동 매칭"}
                    </button>

                    {matchedUsers.length > 0 && (
                      <div className="border border-[#e0e0e0] rounded-[8px] p-3">
                        <p className="text-[12px] text-[#555] mb-2">매칭된 회원:</p>
                        {matchedUsers.map((u: any) => (
                          <div key={u.userId} className="flex items-center justify-between py-2 border-b border-[#f5f5f5] last:border-0">
                            <div className="text-[13px]">
                              <span className="font-medium">{u.name || "-"}</span>
                              <span className="text-[#888] ml-2">{u.email}</span>
                              <span className="text-[#888] ml-2">({u.businessNumber})</span>
                            </div>
                            <button
                              onClick={() => handleLinkUser(u.userId)}
                              className="flex items-center gap-1 px-3 py-1 text-[12px] bg-[#222] text-white rounded-[6px] hover:bg-[#333]"
                            >
                              <Link2 size={12} />연결
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {matchedUsers.length === 0 && !isMatching && (
                      <p className="text-[12px] text-[#888]">
                        사업자번호로 자동 매칭을 시도합니다. 매칭되는 회원이 없을 경우 수동으로 연결할 수 없습니다.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 첨부 파일 탭 */}
          {activeTab === "images" && (
            <div className="bg-white border border-[#e0e0e0] rounded-[12px] p-5">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-[15px] font-medium">첨부 파일</h3>
                <div className="flex items-center gap-2">
                  <select
                    id="imageTypeSelect"
                    defaultValue="BUSINESS_REGISTRATION"
                    className="border border-[#e0e0e0] rounded-[6px] px-2 py-1.5 text-[12px] focus:border-[#4285F4] focus:outline-none"
                  >
                    <option value="BUSINESS_REGISTRATION">사업자등록증</option>
                    <option value="OTHER">기타</option>
                  </select>
                  <label className="flex items-center gap-1 px-3 py-1.5 text-[12px] bg-[#222] text-white rounded-[6px] hover:bg-[#333] cursor-pointer">
                    <Upload size={14} />파일 선택
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        const select = document.getElementById("imageTypeSelect") as HTMLSelectElement;
                        if (file) handleImageUpload(file, select.value);
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>
              </div>

              {partner.images.length === 0 ? (
                <div className="text-center py-10 text-[#888] text-[14px]">첨부된 파일이 없습니다.</div>
              ) : (
                <div className="flex flex-col gap-3">
                  {partner.images.map((img) => (
                    <div key={img.id} className="flex items-center justify-between p-3 border border-[#eee] rounded-[8px]">
                      <div className="flex items-center gap-3">
                        <span className="inline-block px-2 py-0.5 rounded text-[11px] bg-[#E8EFFF] text-[#4285F4]">
                          {IMAGE_TYPE_LABELS[img.imageType] || img.imageType}
                        </span>
                        <span className="text-[13px] text-[#555]">{img.originalFileName || "-"}</span>
                        <span className="text-[11px] text-[#888]">{formatDate(img.createdAt)}</span>
                      </div>
                      <button onClick={() => handleImageDelete(img.id)} className="p-1 text-[#888] hover:text-[#F24A4A]">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 정보 비교 탭 */}
          {activeTab === "compare" && (
            <div className="bg-white border border-[#e0e0e0] rounded-[12px] p-5">
              <h3 className="text-[15px] font-medium mb-4">거래처 정보 vs 회원 정보 비교</h3>
              {!partner.userId ? (
                <div className="text-center py-10 text-[#888] text-[14px]">연결된 회원이 없어 비교할 수 없습니다.</div>
              ) : compareFields.length === 0 ? (
                <div className="text-center py-10 text-[#888] text-[14px]">비교 데이터를 불러오는 중...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[13px]">
                    <thead>
                      <tr className="bg-[#FAFAFA] border-b border-[#eee]">
                        <th className="text-left px-4 py-3 font-medium text-[#555]">항목</th>
                        <th className="text-left px-4 py-3 font-medium text-[#555]">거래처 정보</th>
                        <th className="text-left px-4 py-3 font-medium text-[#555]">회원 정보</th>
                        <th className="text-center px-4 py-3 font-medium text-[#555]">상태</th>
                      </tr>
                    </thead>
                    <tbody>
                      {compareFields.map((field) => (
                        <tr key={field.fieldName} className={`border-b border-[#eee] ${field.isDifferent ? "bg-[#FEF2F2]" : ""}`}>
                          <td className="px-4 py-3 text-[#555] font-medium">{FIELD_LABELS[field.fieldName] || field.fieldName}</td>
                          <td className="px-4 py-3">{field.businessClientValue || "-"}</td>
                          <td className="px-4 py-3">{field.userValue || "-"}</td>
                          <td className="px-4 py-3 text-center">
                            {field.isDifferent
                              ? <span className="text-[#F24A4A] text-[11px] font-medium">불일치</span>
                              : <span className="text-[#4285F4] text-[11px]">일치</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* 거래 내역 탭 */}
          {activeTab === "orders" && (
            <div className="bg-white border border-[#e0e0e0] rounded-[12px] p-5">
              <h3 className="text-[15px] font-medium mb-4">거래 내역</h3>
              {!partner.userId ? (
                <div className="text-center py-10 text-[#888] text-[14px]">
                  회원이 연결되지 않아 거래 내역을 확인할 수 없습니다.<br />
                  <span className="text-[12px]">기업 정보 탭에서 회원을 연결하면 거래 내역을 확인할 수 있습니다.</span>
                </div>
              ) : (
                <>
                  {/* 주문내역 / 주문파일 서브탭 */}
                  <div className="flex gap-2 mb-4">
                    <button
                      onClick={() => setOrderSubTab("orders")}
                      className={`px-3 py-1.5 text-[12px] rounded-[6px] ${
                        orderSubTab === "orders"
                          ? "bg-[#222] text-white"
                          : "bg-white text-[#222] border border-[#222] hover:bg-[#f5f5f5]"
                      }`}
                    >
                      주문 내역
                    </button>
                    <button
                      onClick={() => setOrderSubTab("files")}
                      className={`px-3 py-1.5 text-[12px] rounded-[6px] ${
                        orderSubTab === "files"
                          ? "bg-[#222] text-white"
                          : "bg-white text-[#222] border border-[#222] hover:bg-[#f5f5f5]"
                      }`}
                    >
                      주문 파일
                    </button>
                  </div>

                  {orderSubTab === "orders" && (
                    <div className="text-[12px]">
                      <Table
                        columnWidths={["80px", "120px", "180px", "100px", "120px", "120px"]}
                        tabledata={{
                          head: [
                            <>주문번호</>,
                            <>주문명</>,
                            <>구매자</>,
                            <>상태</>,
                            <>금액</>,
                            <>주문일</>,
                          ],
                          body: orders.map((order) => [
                            <>{order.orderNumber || "-"}</>,
                            <>{order.name || "-"}</>,
                            <>{order.purchaserName || "-"}</>,
                            <>{order.orderStatus || "-"}</>,
                            <>{order.orderTotalAmount?.toLocaleString() || "0"}원</>,
                            <>{formatDate(order.createdAt)}</>,
                          ]),
                        }}
                      />
                      {orders.length === 0 && (
                        <div className="text-center text-[#888] py-10 text-[14px]">
                          주문 내역이 없습니다.
                        </div>
                      )}
                    </div>
                  )}

                  {orderSubTab === "files" && (
                    <div className="text-[12px]">
                      <Table
                        columnWidths={["80px", "120px", "180px", "200px", "120px"]}
                        tabledata={{
                          head: [
                            <>주문번호</>,
                            <>주문명</>,
                            <>파일</>,
                            <>주문일</>,
                          ],
                          body: orderFiles.map((file) => [
                            <>{file.orderNumber || "-"}</>,
                            <>{file.orderName || "-"}</>,
                            <>
                              {file.fileUrl ? (
                                <a
                                  href={file.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#4285F4] hover:underline"
                                >
                                  다운로드
                                </a>
                              ) : (
                                "-"
                              )}
                            </>,
                            <>{formatDate(file.createdAt)}</>,
                          ]),
                        }}
                      />
                      {orderFiles.length === 0 && (
                        <div className="text-center text-[#888] py-10 text-[14px]">
                          주문 파일이 없습니다.
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </CompanyAdminLayout>
    </div>
  );
};

const InfoRow: React.FC<{ label: string; value: string | null }> = ({ label, value }) => (
  <div className="flex items-center py-2 border-b border-[#f5f5f5]">
    <span className="w-[160px] text-[13px] text-[#888] shrink-0">{label}</span>
    <span className="text-[13px] text-[#222]">{value || "-"}</span>
  </div>
);

const EditField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
}> = ({ label, value, onChange }) => (
  <div className="flex items-center gap-3">
    <label className="w-[160px] text-[12px] font-medium text-[#555] shrink-0">{label}</label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="flex-1 border border-[#e0e0e0] rounded-[8px] px-3 py-2 text-[13px] focus:border-[#4285F4] focus:outline-none"
    />
  </div>
);

export default ManagedClientDetail;